import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCheck, Inbox } from 'lucide-react';
import { useState } from 'react';

import type { EscritorioMembro } from '@/api/client';
import { atribuirResponsaveis, type FilaParaDistribuir, type ItemDaFila, type Responsavel } from '@/api/distribuicao';
import type { Equipe } from '@/api/equipes';
import {
  descreverResponsaveis,
  invalidarDistribuicao,
  ResponsaveisDialog,
} from '@/components/escritorio/ResponsaveisDialog';
import { LinkDosAutosEmNovaAba } from '@/components/processos/LinkDosAutos';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Spinner } from '@/components/ui/spinner';
import { useNotification } from '@/context/NotificationProvider';
import { notifyMutationError } from '@/lib/errors';
import { formatDate } from '@/lib/formatters';

import { AplicadasPelasRegras } from './AplicadasPelasRegras';

interface DistribuirAbaProps {
  escritorioId: string;
  fila: FilaParaDistribuir;
  membros: EscritorioMembro[];
  equipes: Equipe[];
}

/** O dado que falta, como o gestor o reconhece. */
const DADO_QUE_FALTA: Record<string, string> = {
  CLASSE: 'a classe',
  ASSUNTO: 'o assunto',
  VALOR: 'o valor da causa',
  ORGAO: 'o órgão julgador',
  MUNICIPIO: 'o município',
  CLIENTE: 'as partes',
  PARTE_DOCUMENTO: 'as partes',
  PARTE_NOME: 'as partes',
  POLO_DO_CLIENTE: 'as partes',
  IA: 'a avaliação da IA',
};

function aguardando(item: ItemDaFila): boolean {
  return item.motivo === 'AGUARDANDO_DADOS';
}

/** Por que a sugestão: a regra do escritório, a equipe do mesmo cliente, ou o dono da OAB. */
function porque(item: ItemDaFila): string {
  if (item.motivo === 'REGRA') {
    const regra = `pela regra “${item.regra_nome ?? ''}”`;
    return item.motivo_ia ? `${regra} — IA: ${item.motivo_ia}` : regra;
  }
  if (aguardando(item)) {
    const dados = [...new Set((item.faltando ?? []).map((c) => DADO_QUE_FALTA[c] ?? c))].join(', ');
    return `A regra “${item.regra_nome ?? ''}” aguarda ${dados || 'os dados do processo'}`;
  }
  if (item.motivo === 'MESMO_CLIENTE') return 'cuida dos outros processos deste cliente';
  return item.dono_nome ? `chegou pela OAB de ${item.dono_nome}` : 'chegou pela OAB do dono';
}

/**
 * A fila "Para distribuir" do Gestor (§19): o que chegou ao escritório e ainda não foi
 * distribuído, com uma sugestão. Enquanto ninguém decide, quem recebe é o dono do processo.
 */
export function DistribuirAba({ escritorioId, fila, membros, equipes }: DistribuirAbaProps) {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const [escolhendo, setEscolhendo] = useState<ItemDaFila | null>(null);
  const [aceitando, setAceitando] = useState<string | null>(null);

  // Aceitar agrupa por sugestão: um pedido por responsável sugerido.
  const aceitar = useMutation({
    mutationFn: async (itens: ItemDaFila[]) => {
      const grupos = new Map<string, { sugestao: Responsavel; ids: string[] }>();
      for (const i of itens) {
        const k = `${i.sugestao.tipo}:${i.sugestao.valor}`;
        const g = grupos.get(k) ?? { sugestao: i.sugestao, ids: [] };
        g.ids.push(i.processo_id);
        grupos.set(k, g);
      }
      let atribuidos = 0;
      for (const g of grupos.values()) {
        atribuidos += (await atribuirResponsaveis(escritorioId, g.ids, [g.sugestao])).atribuidos;
      }
      return atribuidos;
    },
    onSuccess: (n) => showSuccess(n === 1 ? '1 processo distribuído' : `${n} processos distribuídos`),
    onError: (err) => notifyMutationError(err, showError),
    onSettled: () => {
      setAceitando(null);
      invalidarDistribuicao(queryClient, escritorioId);
    },
  });

  const aplicadas = <AplicadasPelasRegras escritorioId={escritorioId} membros={membros} equipes={equipes} />;
  const semCreditos = fila.ia_sem_creditos && (
    <Alert>
      <AlertDescription>
        Os créditos de IA do escritório acabaram neste mês: as regras com critério da IA não estão sugerindo.
      </AlertDescription>
    </Alert>
  );

  if (fila.itens.length === 0) {
    return (
      <div className="space-y-3">
        {semCreditos}
        <EmptyState
          icon={<Inbox />}
          title="Nada para distribuir"
          description="Os processos que chegarem ao escritório aparecem aqui, com uma sugestão de quem cuida."
        />
        {aplicadas}
      </div>
    );
  }

  // Quem aguarda dados fica fora do "aceitar todas": a regra ainda pode mandá-lo a outro lugar.
  const prontos = fila.itens.filter((i) => !aguardando(i));

  return (
    <div className="space-y-3">
      {semCreditos}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">
          {fila.total > fila.itens.length ? `Os ${fila.itens.length} mais recentes de ${fila.total} processos. ` : ''}
          Enquanto ninguém distribui, quem recebe as intimações é o dono do processo.
        </p>
        {prontos.length > 0 && (
          <Button
            variant="outline"
            className="h-11 shrink-0 sm:h-9"
            disabled={aceitar.isPending}
            onClick={() => {
              setAceitando('todos');
              aceitar.mutate(prontos);
            }}
          >
            {aceitar.isPending && aceitando === 'todos' ? (
              <Spinner size={16} className="mr-2 text-inherit" />
            ) : (
              <CheckCheck className="mr-2 h-4 w-4" aria-hidden="true" />
            )}
            {prontos.length === 1 ? 'Aceitar a sugestão' : `Aceitar as ${prontos.length} sugestões`}
          </Button>
        )}
      </div>

      <ul className="space-y-2">
        {fila.itens.map((item) => (
          <li key={item.processo_id}>
            <Card className="flex flex-col gap-3 p-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <p className="font-mono text-sm font-medium [overflow-wrap:anywhere]">
                  <LinkDosAutosEmNovaAba numero={item.numero} />
                </p>
                <p className="text-xs text-muted-foreground [overflow-wrap:anywhere]">
                  {item.classe} · {item.tribunal} · chegou em {formatDate(item.chegou_em)}
                </p>
                {aguardando(item) ? (
                  // A sugestão é provisória: aceitar agora tira o processo da regra que ainda aguarda.
                  <p className="mt-1 text-sm">
                    <span className="text-xs text-muted-foreground">{porque(item)}. </span>
                    Por ora:{' '}
                    <span className="font-medium">{descreverResponsaveis([item.sugestao], equipes, membros)}</span>
                  </p>
                ) : (
                  <p className="mt-1 text-sm">
                    Sugestão:{' '}
                    <span className="font-medium">{descreverResponsaveis([item.sugestao], equipes, membros)}</span>{' '}
                    <span className="text-xs text-muted-foreground">({porque(item)})</span>
                  </p>
                )}
              </div>
              <div className="flex shrink-0 gap-2">
                <Button
                  variant="outline"
                  className="h-11 flex-1 sm:h-9 sm:flex-none"
                  onClick={() => setEscolhendo(item)}
                >
                  Escolher…
                </Button>
                <Button
                  className="h-11 flex-1 sm:h-9 sm:flex-none"
                  disabled={aceitar.isPending}
                  onClick={() => {
                    setAceitando(item.processo_id);
                    aceitar.mutate([item]);
                  }}
                >
                  {aceitar.isPending && aceitando === item.processo_id && (
                    <Spinner size={16} className="mr-2 text-inherit" />
                  )}
                  Aceitar
                </Button>
              </div>
            </Card>
          </li>
        ))}
      </ul>

      {aplicadas}

      <ResponsaveisDialog
        escritorioId={escritorioId}
        processoIds={escolhendo ? [escolhendo.processo_id] : []}
        rotulo={escolhendo?.numero ?? ''}
        open={!!escolhendo}
        onOpenChange={(v) => !v && setEscolhendo(null)}
        membros={membros}
        equipes={equipes}
        iniciais={escolhendo ? [escolhendo.sugestao] : []}
      />
    </div>
  );
}
